import { getUniqueId, ScanPhoto, ScanPhotoSource } from '@kaiizen/kai-scan-template-system';

import { useScanEditingContextSlice } from '../../../apiScanAndTemplateState';
import { useAuth } from '../../../useAuth';
import { useScanPhotoSyncModel } from '../Models';
import { usePhotoGallery } from './PhotoGalleryContext';

/**
 * hook returning a function that uploads a photo to the photo gallery and calls the callback with the photoId
 * @param scanId
 * @returns (photoData: Blob, addPhotoToScanCallback: (photoId: string) => void) => Promise<void>
 */
export const usePhotoUploader = (photoSource: ScanPhotoSource = 'upload') => {
  const { scan, scanId } = useScanEditingContextSlice(['scan', 'scanId']);
  const scanType = scan?.scanType;
  const { setPhoto } = usePhotoGallery();
  const { user } = useAuth();
  const { addScanPhotoToSync } = useScanPhotoSyncModel();

  return async (photoData: Blob, addPhotoToScanCallback: (photo: ScanPhoto) => void) => {
    if (!scanId) {
      throw new Error('No scan id available');
      return;
    }
    const photoId = getUniqueId();

    if (!photoData.type.startsWith('image/')) throw new Error('Only image files are supported');

    await setPhoto(scanId, photoId, photoData); // immediately makes the photo available to the UI
    addPhotoToScanCallback({ photoId, addedAt: Date.now(), addedByUserId: user?.id, photoSource, tags: scanType ? [scanType] : undefined }); // so we can immediately add it to the scan
    await addScanPhotoToSync(scanId, photoId); // add to Local Sync DB - the synchronizer will handle the upload
  };
};

export const usePhotoUploaderFromFileUriV2 = (photoSource: ScanPhotoSource = 'upload') => {
  const { scan, scanId } = useScanEditingContextSlice(['scan', 'scanId']);
  const scanType = scan?.scanType;
  const { setPhotoFromUri } = usePhotoGallery();
  const { addScanPhotoToSync } = useScanPhotoSyncModel();
  const { user } = useAuth();

  return async (photoUri: string, addPhotoToScanCallback: (photo: ScanPhoto) => void) => {
    const photoId = getUniqueId();
    await setPhotoFromUri(scanId, photoId, photoUri); // immediately makes the photo available to the UI
    addPhotoToScanCallback({ photoId, addedAt: Date.now(), addedByUserId: user?.id, photoSource, tags: scanType ? [scanType] : undefined }); // so we can immediately add it to the scan
    await addScanPhotoToSync(scanId, photoId); // add to DB for sync - the synchronizer will handle the upload
  };
};
